import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { redoDepth, undoDepth } from "@tiptap/pm/history";
import {
  Box,
  Button,
  ColorPicker,
  Icon,
  type IconName,
  KeyboardDock,
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Text,
  Toggle,
  Toolbar,
  ToolbarButton,
  ToolbarGroup,
  ToolbarSeparator,
  useIsMobile,
} from "@salestrak/ui";
import { EmojiPicker } from "#app/components/issue-view/activity/emoji-picker";
import { LinkPromptDialog } from "#app/components/editor/link-prompt-dialog";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ToolbarButtonSpec = Omittable<{
  icon: IconName;
  label: string;
  action: () => void;
  isActive?: boolean;
  disabled?: boolean;
  /** When set, the button becomes a Popover trigger with this as content. */
  popover?: {
    content: React.ReactNode;
    open: boolean;
    onOpenChange: (open: boolean) => void;
  };
  menu?: ToolbarMenuItem[];
}>;

type ToolbarMenuItem = Omittable<{
  icon: IconName;
  label: string;
  action: () => void;
  isActive?: boolean;
}>;

type EditorToolbarProps = Omittable<{
  editor: Editor;
  compact?: boolean;
}>;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Overflow math inputs: a ToolbarSeparator column between groups costs its
// 1px line plus the Toolbar's gap on both sides; the "More" trigger is an
// icon-sm button plus one separator gap.
const SEPARATOR_GAP = 17;
const MORE_BUTTON_WIDTH = 36;

function useToolbarState(editor: Editor) {
  return useEditorState({
    editor,
    selector: ({ editor: ed }) => ({
      isBold: ed.isActive("bold"),
      isItalic: ed.isActive("italic"),
      isUnderline: ed.isActive("underline"),
      isStrike: ed.isActive("strike"),
      isCode: ed.isActive("code"),
      // textStyle is the mark Color writes its color onto — lights the color button.
      isTextStyle: ed.isActive("textStyle"),
      // Focus/blur each dispatch a meta transaction, so this tracks live.
      isFocused: ed.isFocused,
      isBulletList: ed.isActive("bulletList"),
      isOrderedList: ed.isActive("orderedList"),
      isTaskList: ed.isActive("taskList"),
      isBlockquote: ed.isActive("blockquote"),
      isCodeBlock: ed.isActive("codeBlock"),
      isLink: ed.isActive("link"),
      isParagraph: ed.isActive("paragraph"),
      isH1: ed.isActive("heading", { level: 1 }),
      isH2: ed.isActive("heading", { level: 2 }),
      isH3: ed.isActive("heading", { level: 3 }),
      isH4: ed.isActive("heading", { level: 4 }),
      isAlignLeft: ed.isActive({ textAlign: "left" }),
      isAlignCenter: ed.isActive({ textAlign: "center" }),
      isAlignRight: ed.isActive({ textAlign: "right" }),
      // Depth reads are O(1); can().undo() rebuilds the whole command map twice
      // per transaction.
      canUndo: undoDepth(ed.state) > 0,
      canRedo: redoDepth(ed.state) > 0,
    }),
  });
}

type ToolbarState = ReturnType<typeof useToolbarState>;

/**
 * One toolbar control. Pressed-state commands render as a base Toggle,
 * plain actions as a ghost icon Button — both through ToolbarButton so the
 * Toolbar's roving focus owns them (the base toolbar idiom).
 */
function ToolbarButtonView({ btn }: { btn: ToolbarButtonSpec }) {
  const isToggle = btn.isActive !== undefined;
  const face = (
    <ToolbarButton
      aria-label={btn.label}
      disabled={btn.disabled}
      onClick={btn.popover || btn.menu ? undefined : btn.action}
      render={
        isToggle ? (
          <Toggle pressed={!!btn.isActive} size="sm" />
        ) : (
          <Button variant="ghost" size="icon-sm" />
        )
      }
    >
      <Icon name={btn.icon} size="md" />
    </ToolbarButton>
  );

  if (btn.menu) {
    return (
      <Menu>
        <MenuTrigger render={face} />
        <MenuPopup align="start" minWidth={180}>
          {btn.menu.map((item) => (
            <MenuItem key={item.label} onClick={item.action}>
              <Icon name={item.icon} size="md" color="muted" />
              <Text>{item.label}</Text>
              {item.isActive && (
                <Icon className="ms-auto" name="check" size="sm" color="muted" />
              )}
            </MenuItem>
          ))}
        </MenuPopup>
      </Menu>
    );
  }

  if (!btn.popover) return face;

  return (
    <Popover open={btn.popover.open} onOpenChange={btn.popover.onOpenChange}>
      <PopoverTrigger render={face} />
      <PopoverPopup side="bottom" align="start">
        {btn.popover.content}
      </PopoverPopup>
    </Popover>
  );
}

function useToolbarGroups(
  editor: Editor,
  state: ToolbarState,
  compact: boolean,
  requestLink: () => void,
  phone: boolean,
): { groups: ToolbarButtonSpec[][]; overlayOpen: boolean } {
  const [emojiOpen, setEmojiOpen] = useState(false);
  // colorValue is the picker's HSL string ("H S% L%"); setColor stores a full hsl(...).
  const [colorOpen, setColorOpen] = useState(false);
  const [colorValue, setColorValue] = useState("0 0% 0%");

  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      editor.chain().focus().insertContent(emoji).run();
      setEmojiOpen(false);
    },
    [editor],
  );

  const emojiPopover = useMemo(
    () => ({
      content: <EmojiPicker onSelect={handleEmojiSelect} />,
      open: emojiOpen,
      onOpenChange: setEmojiOpen,
    }),
    [emojiOpen, handleEmojiSelect],
  );

  const handleColorChange = useCallback(
    (hsl: string) => {
      setColorValue(hsl);
      const [h, s, l] = hsl.replace(/%/g, "").split(/\s+/).map(Number);
      editor.chain().focus().setColor(`hsl(${h}, ${s}%, ${l}%)`).run();
    },
    [editor],
  );

  const handleColorClear = useCallback(() => {
    editor.chain().focus().unsetColor().run();
  }, [editor]);

  const colorPopover = useMemo(
    () => ({
      content: (
        <Box direction="column" gap="sm" align="center">
          <ColorPicker value={colorValue} onChange={handleColorChange} size={180} />
          <Button variant="ghost" size="sm" onClick={handleColorClear}>
            Remove color
          </Button>
        </Box>
      ),
      open: colorOpen,
      onOpenChange: setColorOpen,
    }),
    [colorOpen, colorValue, handleColorChange, handleColorClear],
  );

  const groups = useMemo<ToolbarButtonSpec[][]>(() => {
    // Capability gating: only offer buttons whose command is registered —
    // invoking an unregistered command (undefined on the chain) throws.
    const canTaskList = "toggleTaskList" in editor.commands;
    const canTextAlign = "setTextAlign" in editor.commands;
    const canIndent = "increaseIndent" in editor.commands;
    const canColor = "setColor" in editor.commands;

    const listDropdown: ToolbarButtonSpec = {
      icon: "list",
      label: "Lists",
      action: () => {},
      isActive: state.isBulletList || state.isOrderedList || state.isTaskList,
      menu: [
        {
          icon: "list",
          label: "Bullet list",
          action: () => editor.chain().focus().toggleBulletList().run(),
          isActive: state.isBulletList,
        },
        {
          icon: "list-ordered",
          label: "Numbered list",
          action: () => editor.chain().focus().toggleOrderedList().run(),
          isActive: state.isOrderedList,
        },
        ...(canTaskList
          ? [
              {
                icon: "list-checks" as IconName,
                label: "Checklist",
                action: () => editor.chain().focus().toggleTaskList().run(),
                isActive: state.isTaskList,
              },
            ]
          : []),
      ],
    };

    const textStyleDropdown: ToolbarButtonSpec = {
      icon: "heading-1",
      label: "Text style",
      action: () => {},
      isActive: state.isH1 || state.isH2 || state.isH3 || state.isH4,
      menu: [
        {
          icon: "pilcrow",
          label: "Normal",
          action: () => editor.chain().focus().setParagraph().run(),
          isActive: state.isParagraph,
        },
        {
          icon: "heading-1",
          label: "Heading 1",
          action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
          isActive: state.isH1,
        },
        {
          icon: "heading-2",
          label: "Heading 2",
          action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
          isActive: state.isH2,
        },
        {
          icon: "heading-3",
          label: "Heading 3",
          action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
          isActive: state.isH3,
        },
        {
          icon: "heading-4",
          label: "Heading 4",
          action: () => editor.chain().focus().toggleHeading({ level: 4 }).run(),
          isActive: state.isH4,
        },
      ],
    };

    const marks: ToolbarButtonSpec[] = [
      {
        icon: "bold",
        label: "Bold",
        action: () => editor.chain().focus().toggleBold().run(),
        isActive: state.isBold,
      },
      {
        icon: "italic",
        label: "Italic",
        action: () => editor.chain().focus().toggleItalic().run(),
        isActive: state.isItalic,
      },
      {
        icon: "underline",
        label: "Underline",
        action: () => editor.chain().focus().toggleUnderline().run(),
        isActive: state.isUnderline,
      },
      {
        icon: "strikethrough",
        label: "Strikethrough",
        action: () => editor.chain().focus().toggleStrike().run(),
        isActive: state.isStrike,
      },
      {
        icon: "code",
        label: "Inline code",
        action: () => editor.chain().focus().toggleCode().run(),
        isActive: state.isCode,
      },
      ...(canColor
        ? [
            {
              icon: "palette" as IconName,
              label: "Text color",
              action: () => setColorOpen((v) => !v),
              isActive: state.isTextStyle,
              popover: colorPopover,
            },
          ]
        : []),
    ];

    const alignmentItems: ToolbarMenuItem[] = canTextAlign
      ? [
          {
            icon: "align-left",
            label: "Left",
            action: () => editor.chain().focus().setTextAlign("left").run(),
            isActive: state.isAlignLeft,
          },
          {
            icon: "align-center",
            label: "Center",
            action: () => editor.chain().focus().setTextAlign("center").run(),
            isActive: state.isAlignCenter,
          },
          {
            icon: "align-right",
            label: "Right",
            action: () => editor.chain().focus().setTextAlign("right").run(),
            isActive: state.isAlignRight,
          },
        ]
      : [];
    const indentItems: ToolbarMenuItem[] = canIndent
      ? [
          {
            icon: "indent-decrease",
            label: "Indent out",
            action: () => {
              if (editor.isActive("listItem") || editor.isActive("taskItem")) {
                editor.chain().focus().liftListItem("listItem").run();
              } else {
                editor.chain().focus().decreaseIndent().run();
              }
            },
          },
          {
            icon: "indent-increase",
            label: "Indent in",
            action: () => {
              if (editor.isActive("listItem") || editor.isActive("taskItem")) {
                editor.chain().focus().sinkListItem("listItem").run();
              } else {
                editor.chain().focus().increaseIndent().run();
              }
            },
          },
        ]
      : [];
    const alignmentDropdown: ToolbarButtonSpec | null =
      alignmentItems.length || indentItems.length
        ? {
            icon: "align-left",
            label: "Alignment",
            action: () => {},
            isActive: state.isAlignCenter || state.isAlignRight,
            menu: [...alignmentItems, ...indentItems],
          }
        : null;

    const quoteAndLink: ToolbarButtonSpec[] = [
      {
        icon: "quote",
        label: "Quote",
        action: () => editor.chain().focus().toggleBlockquote().run(),
        isActive: state.isBlockquote,
      },
      {
        icon: "link",
        label: "Insert a link",
        action: requestLink,
        isActive: state.isLink,
      },
    ];

    const insertDropdown: ToolbarButtonSpec = {
      icon: "plus",
      label: "Insert",
      action: () => {},
      menu: [
        {
          icon: "table",
          label: "Table",
          action: () =>
            editor
              .chain()
              .focus()
              .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
              .run(),
        },
        {
          icon: "minus",
          label: "Divider",
          action: () => editor.chain().focus().setHorizontalRule().run(),
        },
        {
          icon: "square-code",
          label: "Code block",
          action: () => editor.chain().focus().toggleCodeBlock().run(),
        },
      ],
    };

    const emojiButton: ToolbarButtonSpec = {
      icon: "smile-plus",
      label: "Emoji",
      action: () => setEmojiOpen((v) => !v),
      popover: emojiPopover,
    };

    // A phone bar hangs off the keyboard, so it carries only what a thumb
    // reaches for while writing, as direct toggles: a menu or popover takes
    // focus off the editor and drops the keyboard under the finger. Undo is
    // the system's (shake, three-finger swipe); headings, colour, code and
    // the rest stay one "/" away.
    if (phone) {
      const listToggles = (listDropdown.menu ?? []).map((item) => ({
        icon: item.icon,
        label: item.label,
        action: item.action,
        isActive: !!item.isActive,
      }));
      return [marks.slice(0, 2), listToggles, quoteAndLink];
    }

    if (compact) {
      return [
        marks,
        [listDropdown, ...(alignmentDropdown ? [alignmentDropdown] : [])],
        [...quoteAndLink, emojiButton],
      ];
    }

    return [
      [listDropdown, textStyleDropdown],
      marks,
      ...(alignmentDropdown ? [[alignmentDropdown]] : []),
      quoteAndLink,
      [insertDropdown, emojiButton],
    ];
  }, [editor, state, compact, requestLink, phone, emojiPopover, colorPopover]);

  return { groups, overlayOpen: emojiOpen || colorOpen };
}

type EditorToolbarContentProps = Omittable<{
  editor: Editor;
  compact: boolean;
  /** When false, the overflow math is skipped (floating shell — fixed set). */
  responsive?: boolean;
  /** Strip the Toolbar's card chrome (the keyboard dock paints its own fill
   *  and hairline and spans the viewport edge to edge). */
  flush?: boolean;
  /** Reports whether a popup the bar opened (emoji, color, link prompt) is
   *  up — those take focus off the editor, and a host that shows the bar only
   *  while the editor is engaged must count them as engagement. */
  onOverlayOpenChange?: (open: boolean) => void;
  /** The touch set: the few toggles a keyboard's accessory row can hold. */
  phone?: boolean;
  /** Host actions that lead the bar as their own group (attach, mention). */
  leading?: ToolbarButtonSpec[];
}>;

function EditorToolbarContent({
  editor,
  compact,
  responsive = true,
  flush = false,
  onOverlayOpenChange,
  phone = false,
  leading,
}: EditorToolbarContentProps) {
  const state = useToolbarState(editor);

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const requestLink = useCallback(() => {
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    setLinkDialogOpen(true);
  }, [editor]);
  const handleLinkSubmit = useCallback(
    (url: string) => {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    },
    [editor],
  );

  const { groups, overlayOpen } = useToolbarGroups(editor, state, compact, requestLink, phone);
  const anyOverlayOpen = overlayOpen || linkDialogOpen;
  useEffect(() => {
    onOverlayOpenChange?.(anyOverlayOpen);
  }, [anyOverlayOpen, onOverlayOpenChange]);

  const historyGroup = useMemo<ToolbarButtonSpec[]>(
    () => [
      {
        icon: "undo",
        label: "Undo",
        action: () => editor.chain().focus().undo().run(),
        disabled: !state.canUndo,
      },
      {
        icon: "redo",
        label: "Redo",
        action: () => editor.chain().focus().redo().run(),
        disabled: !state.canRedo,
      },
    ],
    [editor, state.canUndo, state.canRedo],
  );

  const allGroups = useMemo(
    () => [
      ...(leading?.length ? [leading] : []),
      ...groups,
      ...(phone ? [] : [historyGroup]),
    ],
    [leading, groups, phone, historyGroup],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const groupRefs = useRef<(HTMLElement | null)[]>([]);
  const groupWidths = useRef<number[]>([]);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  const prevGroupsLength = useRef(allGroups.length);
  if (prevGroupsLength.current !== allGroups.length) {
    prevGroupsLength.current = allGroups.length;
    groupWidths.current = [];
  }

  const needsMeasurement = responsive && groupWidths.current.length !== allGroups.length;

  const calculateFromStoredWidths = useCallback(() => {
    const el = containerRef.current;
    if (!el || groupWidths.current.length === 0) return;
    const containerWidth = el.clientWidth;
    let totalWidth = 0;
    let count = 0;
    for (const [i, groupWidth] of groupWidths.current.entries()) {
      const withSeparator = i > 0 ? groupWidth + SEPARATOR_GAP : groupWidth;
      const hasMore = i < allGroups.length - 1;
      const available = containerWidth - (hasMore ? MORE_BUTTON_WIDTH + SEPARATOR_GAP : 0);
      if (totalWidth + withSeparator > available) break;
      totalWidth += withSeparator;
      count++;
    }
    setVisibleCount(count >= allGroups.length ? allGroups.length : Math.max(1, count));
  }, [allGroups.length]);

  useLayoutEffect(() => {
    if (!needsMeasurement) return;
    groupWidths.current = groupRefs.current
      .slice(0, allGroups.length)
      .map((el) => el?.offsetWidth ?? 0);
    calculateFromStoredWidths();
  });

  useEffect(() => {
    if (!responsive) return;
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(calculateFromStoredWidths);
    observer.observe(el);
    return () => observer.disconnect();
  }, [calculateFromStoredWidths, responsive]);

  const effectiveCount = !responsive
    ? allGroups.length
    : needsMeasurement
      ? allGroups.length
      : (visibleCount ?? allGroups.length);
  const hasOverflow = responsive && !needsMeasurement && effectiveCount < allGroups.length;

  return (
    <>
      <Toolbar
        ref={containerRef}
        className={flush ? "rounded-none border-none bg-transparent" : undefined}
      >
        {allGroups.map((group, gi) => {
          if (gi >= effectiveCount) return null;
          return (
            <React.Fragment key={gi}>
              {gi > 0 && <ToolbarSeparator />}
              <ToolbarGroup
                ref={(el: HTMLDivElement | null) => {
                  groupRefs.current[gi] = el;
                }}
              >
                {group.map((btn) => (
                  <ToolbarButtonView key={btn.label} btn={btn} />
                ))}
              </ToolbarGroup>
            </React.Fragment>
          );
        })}

        {hasOverflow && (
          <>
            <ToolbarSeparator />
            <Menu>
              <MenuTrigger
                render={
                  <ToolbarButton
                    aria-label="More formatting options"
                    render={<Button variant="ghost" size="icon-sm" />}
                  />
                }
              >
                <Icon name="ellipsis" size="md" />
              </MenuTrigger>
              <MenuPopup align="end" minWidth={200}>
                {allGroups.slice(effectiveCount).map((group, gi) => (
                  <React.Fragment key={gi}>
                    {gi > 0 && <MenuSeparator />}
                    {group.map((btn) =>
                      // Dropdown buttons have a no-op action; flatten their
                      // sub-items under a label so they stay reachable on overflow.
                      btn.menu ? (
                        <MenuGroup key={btn.label}>
                          <MenuGroupLabel>{btn.label}</MenuGroupLabel>
                          {btn.menu.map((item) => (
                            <MenuItem key={item.label} onClick={item.action}>
                              <Icon name={item.icon} size="md" color="muted" />
                              <Text>{item.label}</Text>
                            </MenuItem>
                          ))}
                        </MenuGroup>
                      ) : (
                        <MenuItem key={btn.label} onClick={btn.action} disabled={btn.disabled}>
                          <Icon name={btn.icon} size="md" color="muted" />
                          <Text>{btn.label}</Text>
                        </MenuItem>
                      ),
                    )}
                  </React.Fragment>
                ))}
              </MenuPopup>
            </Menu>
          </>
        )}
      </Toolbar>
      <LinkPromptDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        onSubmit={handleLinkSubmit}
      />
    </>
  );
}

export function EditorToolbar({
  editor,
  compact = false,
}: EditorToolbarProps) {
  const isMobile = useIsMobile();
  const isFocused = useEditorState({
    editor,
    selector: ({ editor: ed }) => ed.isFocused,
  });
  const [overlayOpen, setOverlayOpen] = useState(false);

  // The menu centers on the selection, so a selection at the editor's edge
  // would carry it past the host (a narrow note card) and under whatever
  // chrome sits beside it. Shift keeps it within the editor's own box; when
  // the editor is narrower than the menu, the menu pins to the editor's start
  // edge so the overflow lands on the one side the reader can still reach.
  const bubbleOptions = useMemo(
    () => ({
      placement: "top" as const,
      strategy: "fixed" as const,
      offset: 8,
      shift: { boundary: editor.view.dom, padding: 0 },
    }),
    [editor],
  );

  // Body-portaled, so it sits outside a host modal dialog that sets
  // pointer-events:none on <body> (inherited → clicks pass through). Re-enable
  // pointers, and preventDefault on mousedown so pressing a button doesn't blur
  // the editor and drop the selection the formatting action targets.
  const setBubbleEl = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    el.style.pointerEvents = "auto";
    el.addEventListener("mousedown", (e) => e.preventDefault());
  }, []);

  // On a phone the toolbar is the keyboard's accessory: KeyboardDock pins it
  // to the bottom of the visual viewport, directly above the on-screen keys,
  // the way a notes app hangs its formatting row off the keyboard rather than
  // off the note. It shows only while the editor is ENGAGED — focused, or with
  // one of the bar's own popups up — so an idle note reads as content, and
  // since only one editor holds focus at a time there is one dock per page.
  // The bar is MOUNTED on engagement rather than hidden: its overflow fold
  // measures group widths on mount, and a hidden mount measures zeros.
  if (isMobile) {
    if (!isFocused && !overlayOpen) return null;
    return (
      <KeyboardDock
        // A press on the bar must not blur the editor: the blur would unmount
        // the dock under the finger before its click lands.
        onMouseDown={(e) => e.preventDefault()}
      >
        <EditorToolbarContent
          editor={editor}
          compact={compact}
          flush
          phone
          onOverlayOpenChange={setOverlayOpen}
        />
      </KeyboardDock>
    );
  }

  return (
    // Portal to <body>: the default appendTo traps the menu inside the
    // composer's scroll container, where position:absolute is clipped.
    <BubbleMenu
      editor={editor}
      pluginKey="textMenu"
      appendTo={() => document.body}
      options={bubbleOptions}
    >
      <Box ref={setBubbleEl}>
        <EditorToolbarContent editor={editor} compact={compact} responsive={false} />
      </Box>
    </BubbleMenu>
  );
}

type EditorFormatStripProps = Omittable<{
  editor: Editor;
  /** Host actions ahead of the formatting toggles (attach, mention). */
  leading?: ToolbarButtonSpec[];
}>;

/**
 * The phone formatting set as a bare strip, for a host that seats the
 * editor's tools itself — a composer docked on the keyboard, where a second
 * bar of our own would stack on the browser's accessory row. A press on the
 * strip keeps the editor's focus (and so the keyboard) where it is.
 */
export function EditorFormatStrip({ editor, leading }: EditorFormatStripProps) {
  return (
    <Box overflowX="auto" scrollbar="hidden" onMouseDown={(e) => e.preventDefault()}>
      <EditorToolbarContent
        editor={editor}
        compact
        flush
        phone
        responsive={false}
        leading={leading}
      />
    </Box>
  );
}
